import type { Pattern } from '../types/state';
import type { IRPattern } from '../types/ir';
import { generateDsl } from './dsl-generator';
import { cloneIRNodePreservingIds } from './fork';

export const BUILTIN_JUST_KNIT_ID = 'builtin-just-knit';
const JUST_KNIT_ITER = 'r';

const BUILTIN_JUST_KNIT_STRUCTURE: IRPattern = {
  id: 'builtin-just-knit-root',
  kind: 'pattern',
  ir_version: '1.0',
  name: 'Just Knit',
  params: [],
  counters: [],
  segments: [
    {
      id: 'builtin-just-knit-seg-1',
      kind: 'segment',
      name: 'Section 1',
      counters: [],
      setup: [],
      body: [
        {
          id: 'builtin-just-knit-grp',
          kind: 'group',
          iter_name: JUST_KNIT_ITER,
          count: {
            kind: 'count_until_text',
            text_expr: { kind: 'literal', type: 'text', value: 'done' },
          },
          counters: [],
          body: [
            {
              id: 'builtin-just-knit-row',
              kind: 'row',
              instruction: {
                kind: 'instruction_text',
                expr: {
                  kind: 'binop',
                  op: '+',
                  left: { kind: 'literal', type: 'text', value: 'Knit row ' },
                  right: { kind: 'counter_ref', name: JUST_KNIT_ITER },
                },
              },
              deltas: [],
            },
          ],
        },
      ],
    },
  ],
};

/**
 * The canonical Just Knit pattern, seeded into the user's library on first
 * app load. Marked `builtin: true` so it's hidden from the library and
 * pattern picker; its only purpose is to serve as the parent that every
 * Just Knit project forks from. Users never edit it directly.
 */
export const BUILTIN_JUST_KNIT_PATTERN: Pattern = {
  id: BUILTIN_JUST_KNIT_ID,
  title: 'Just Knit',
  parentPatternId: null,
  structure: BUILTIN_JUST_KNIT_STRUCTURE,
  dsl: generateDsl(BUILTIN_JUST_KNIT_STRUCTURE),
  builtin: true,
};

/**
 * Builds a project-private fork of the canonical Just Knit pattern with the
 * given project name. The fork has fresh node IDs throughout and
 * `parentPatternId` set to the canonical, so subsequent edits go in-place
 * (the standard fork-or-edit detection treats it as already private).
 */
export function createJustKnitProjectFork(projectName: string): Pattern {
  const structure = cloneIRNodePreservingIds(BUILTIN_JUST_KNIT_STRUCTURE, new Set());
  structure.name = projectName;
  return {
    id: crypto.randomUUID(),
    title: projectName,
    parentPatternId: BUILTIN_JUST_KNIT_ID,
    structure,
    dsl: generateDsl(structure),
  };
}
